import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { useTranslation } from 'react-i18next'
import i18nInstance from './i18n'
import './index.css'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { App as AntdApp, ConfigProvider } from 'antd'

import LoginPage from './pages/Login'
import ServicesPage from './pages/Services'
import UsersPage from './pages/Users'
import ArticlesPage from './pages/Articles'

import Cookies from 'js-cookie'
import { clearSession, setSession } from './api'
import { SyncLoader } from 'react-spinners'
import deDE from 'antd/locale/de_DE'
import enUS from 'antd/locale/en_US'
import VerificationPage from './pages/Verfication'
import ErrorBoundary from './components/ErrorBoundary'
import i18n from 'i18next'
import { afterLoginTarget, loginUrl } from './utils/loginRedirect'

const root = ReactDOM.createRoot(document.getElementById('root'))

const isDev = import.meta.env.DEV
const backendUrl = isDev ? 'http://localhost:3002' : 'https://app.horoluma.com'

global.WatchStorage = {
  session: null,
  backendUrl,
}

const antdLocales = { de_DE: deDE, en_US: enUS }

const NotFoundPage = () => {
  useTranslation()
  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <img src="/favicon.svg" style={{ height: 64, marginBottom: 16 }} />
      <h1>{i18n('notFound.title')}</h1>
      <h2 style={{ fontWeight: 400 }}>{i18n('notFound.message')}</h2>
    </div>
  )
}

/**
 * HOR-92 (Review ADM-4 / F1): Ohne Session zum Login – das angeforderte Ziel (z.B.
 * /services?open=123 aus einer Benachrichtigungs-Mail) wandert als ?redirect= mit, sonst
 * ginge der Deep Link durch das `replace` verloren und der Login landete fest auf /services.
 */
const LoginRedirect = ({ reason }) => {
  const location = useLocation()

  return <Navigate to={loginUrl({ reason, redirect: `${location.pathname}${location.search}${location.hash}` })} replace />
}

// HOR-92 (F1): /login mit gueltiger Session – zum gemerkten Ziel statt fest nach /services.
const LoggedInRedirect = () => {
  const location = useLocation()

  return <Navigate to={afterLoginTarget(location.search)} replace />
}

const Application = () => {
  const { i18n: i18nRes } = useTranslation()
  const [validSession, setValidSession] = useState(null)
  const [redirectReason, setRedirectReason] = useState(null)
  const lang = i18nRes.language || 'de_DE'

  useEffect(() => {
    const sessionCookie = Cookies.get('session')

    if (!sessionCookie) {
      setValidSession(false)
      return
    }

    let session = null

    try {
      session = JSON.parse(sessionCookie)
    } catch (e) {
      // corrupt cookie must never white-screen the app – drop it and go to login
      clearSession()
      setValidSession(false)
      return
    }

    if (!session?.token) {
      clearSession()
      setValidSession(false)
      return
    }

    fetch(`${global.WatchStorage.backendUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.token}`,
      },
    })
      .then(async data => {
        if (data.status === 201) {
          const user = await data.json()

          if (user.role && user.role !== 'administrator') {
            clearSession()
            setRedirectReason('noPermission')
            setValidSession(false)
            return
          }

          global.WatchStorage.session = user

          if (user.language) await i18nInstance.changeLanguage(user.language)

          setSession(user)

          setValidSession(true)
        } else {
          clearSession()
          setValidSession(false)
        }
      })
      .catch(() => {
        global.WatchStorage.session = null
        setValidSession(false)
      })
  }, [])

  if (validSession === null)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#eee',
          width: '100vw',
          height: '100vh',
        }}
      >
        <SyncLoader size={24} color="#2c3e50" />
      </div>
    )

  return (
    <ConfigProvider
      locale={antdLocales[lang] || deDE}
      theme={{
        components: {
          Layout: {
            siderBg: '#2c3e50',
          },
          Menu: {
            darkItemBg: '#2c3e50',
            darkItemSelectedBg: '#7f8c8d',
            itemBorderRadius: 4,
            itemMarginInline: 12,
            itemHeight: 48,
            iconSize: 20,
            fontSize: 16,
          },
        },
        token: {
          colorPrimary: '#2c3e50',
        },
      }}
    >
      <AntdApp>
        <BrowserRouter>
          <Routes>
            {validSession && (
              <>
                <Route path="/*" element={<NotFoundPage />} />
                <Route path="/" element={<ServicesPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/verification" element={<VerificationPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/articles" element={<ArticlesPage />} />
              </>
            )}
            {!validSession && <Route path="/*" element={<LoginRedirect reason={redirectReason} />} />}
            <Route path="/login" element={validSession ? <LoggedInRedirect /> : <LoginPage />} />
          </Routes>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  )
}

root.render(
  <ErrorBoundary>
    <Application />
  </ErrorBoundary>,
)
